import { INDUSTRIES } from "@/data/content";
import { STATE_DATA, SUPPORTED_SLUGS } from "@/data/serviceAreas";
import { CATEGORIES } from "@/data/education/categories";
import { LEARNING_PATHS } from "@/data/education/learningPaths";
import { GLOSSARY } from "@/data/education/glossary";
import { ARTICLES_1 } from "@/data/education/articles.electricity-gas";
import { ARTICLES_2 } from "@/data/education/articles.bills";
import { ARTICLES_3 } from "@/data/education/articles.markets";

export { CATEGORIES, LEARNING_PATHS, GLOSSARY };

const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.slug, c.label]));

export const ARTICLES = [...ARTICLES_1, ...ARTICLES_2, ...ARTICLES_3].map((a) => ({
  ...a,
  categoryLabel: CATEGORY_LABEL[a.category] || a.category,
}));

export function getArticle(slug) {
  return ARTICLES.find((a) => a.slug === slug) || null;
}
export function getCategory(slug) {
  return CATEGORIES.find((c) => c.slug === slug) || null;
}
export function getPath(slug) {
  return LEARNING_PATHS.find((p) => p.slug === slug) || null;
}
export function getGlossaryTerm(slug) {
  return GLOSSARY.find((g) => g.slug === slug) || null;
}

export function articlesByCategory(slug) {
  return ARTICLES.filter((a) => a.category === slug);
}

export function recentArticles(n = 6) {
  return [...ARTICLES].sort((a, b) => new Date(b.added) - new Date(a.added)).slice(0, n);
}
export function popularArticles(n = 5) {
  const pop = ARTICLES.filter((a) => a.popular);
  return (pop.length ? pop : ARTICLES).slice(0, n);
}
export function featuredArticles(n = 4) {
  const feat = ARTICLES.filter((a) => a.featured);
  return (feat.length ? feat : ARTICLES).slice(0, n);
}

export function relatedArticleObjects(article, n = 4) {
  const slugs = article?.related?.articles || [];
  const list = slugs.map(getArticle).filter(Boolean);
  if (list.length < n) {
    ARTICLES.filter(
      (a) => a.category === article.category && a.slug !== article.slug && !slugs.includes(a.slug)
    ).forEach((a) => list.push(a));
  }
  return list.slice(0, n);
}

function blocksToText(blocks = []) {
  return blocks
    .map((b) => {
      if (b.p) return b.p;
      if (b.h3) return b.h3;
      if (b.note) return b.note;
      if (b.ul) return b.ul.join(". ");
      if (b.ol) return b.ol.join(". ");
      return "";
    })
    .filter(Boolean)
    .join(" ");
}

export function articlePlainText(article) {
  if (!article) return "";
  const parts = [article.excerpt || "", article.intro || ""];
  (article.sections || []).forEach((s) => {
    if (s.heading) parts.push(s.heading);
    parts.push(blocksToText(s.blocks));
  });
  (article.faqs || []).forEach((f) => parts.push(`${f.q} ${f.a}`));
  return parts.filter(Boolean).join("\n");
}

export function rankArticles(query, n = 6) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return [];
  const terms = q.split(/\s+/).filter((t) => t.length > 2);
  const scored = ARTICLES.map((a) => {
    const title = a.title.toLowerCase();
    const hay = [a.title, a.excerpt, a.categoryLabel, (a.keywords || []).join(" "), a.intro]
      .join(" ")
      .toLowerCase();
    let score = 0;
    if (hay.includes(q)) score += 5;
    terms.forEach((t) => {
      if (hay.includes(t)) score += 1;
      if (title.includes(t)) score += 2;
      if ((a.keywords || []).some((k) => k.toLowerCase().includes(t))) score += 2;
    });
    return { a, score };
  })
    .filter((x) => x.score > 0)
    .sort((x, y) => y.score - x.score);
  return scored.slice(0, n).map((x) => x.a);
}

const match = (q, fields) => {
  const hay = fields.filter(Boolean).map(String).join(" ").toLowerCase();
  if (hay.includes(q)) return 10;
  const terms = q.split(/\s+/).filter((t) => t.length >= 3);
  const hits = terms.filter((t) => hay.includes(t)).length;
  return hits;
};

export const SEARCH_TYPES = [
  { key: "all", label: "Everything" },
  { key: "article", label: "Articles" },
  { key: "category", label: "Categories" },
  { key: "path", label: "Learning Paths" },
  { key: "glossary", label: "Glossary" },
  { key: "guide", label: "State & Industry Guides" },
];

export function searchAll(query, type = "all") {
  const q = (query || "").trim().toLowerCase();
  if (!q) return [];
  const out = [];
  const add = (score, entry) => {
    if (score > 0) out.push({ ...entry, _score: score });
  };

  if (type === "all" || type === "article") {
    ARTICLES.forEach((a) => {
      add(match(q, [a.title, a.excerpt, a.categoryLabel, ...(a.keywords || [])]), { type: "Article", title: a.title, subtitle: a.excerpt, path: `/education/${a.slug}`, tag: a.categoryLabel });
    });
  }
  if (type === "all" || type === "category") {
    CATEGORIES.forEach((c) => {
      add(match(q, [c.label, c.description, c.tagline]), { type: "Category", title: c.label, subtitle: c.tagline, path: c.external || `/education/category/${c.slug}`, tag: "Category" });
    });
  }
  if (type === "all" || type === "path") {
    LEARNING_PATHS.forEach((p) => {
      add(match(q, [p.title, p.summary]), { type: "Learning Path", title: p.title, subtitle: p.summary, path: `/education/paths/${p.slug}`, tag: "Learning Path" });
    });
  }
  if (type === "all" || type === "glossary") {
    GLOSSARY.forEach((g) => {
      add(match(q, [g.term, g.short, g.definition]), { type: "Glossary", title: g.term, subtitle: g.short, path: `/education/glossary/${g.slug}`, tag: "Glossary" });
    });
  }
  if (type === "all" || type === "guide") {
    INDUSTRIES.forEach((i) => {
      add(match(q, [i.name, i.summary]), { type: "Industry Guide", title: `${i.name} Energy Guide`, subtitle: i.summary, path: `/industries/${i.slug}`, tag: "Industry Guide" });
    });
    SUPPORTED_SLUGS.forEach((slug) => {
      const s = STATE_DATA[slug];
      add(match(q, [s.name, s.abbr, s.grid]), { type: "State Guide", title: `Commercial Energy in ${s.name}`, subtitle: `${s.grid ? s.grid + " · " : ""}Service availability and market guidance`, path: `/service-areas/${slug}`, tag: "State Guide" });
    });
  }
  return out.sort((a, b) => b._score - a._score).map(({ _score, ...rest }) => rest);
}
